// DescriptionFilterDropdown must NEVER mutate global filter state directly.
// It should ONLY use setSelectedKeys and confirm provided by AntD Table.
// This ensures all filters are preserved and managed by AntD Table.
import React, { useState, useMemo, useEffect } from "react";
import { Button, Space } from "antd";

export default function DescriptionFilterDropdown({ setSelectedKeys, selectedKeys = [], confirm, clearFilters, filterValues, setTableFilters }) {
  if (setTableFilters) {
    // eslint-disable-next-line no-console
    console.warn("[DescriptionFilterDropdown] Do NOT pass setTableFilters. Use setSelectedKeys + confirm only.");
  }

  const [search, setSearch] = useState("");
  const [pendingKeys, setPendingKeys] = useState(selectedKeys);
  const [ searchText, setSearchText ] = useState( "" );
  

  
  // Initialize states from selectedKeys when dropdown is opened
  useEffect(() => {
    if (selectedKeys && selectedKeys.length > 0) {
      if (typeof selectedKeys[0] === 'string') {
        setPendingKeys(prev => prev.length > 0 ? prev : selectedKeys);
      } else if (selectedKeys.some(k => typeof k === 'object')) {
        const stringKeys = selectedKeys.filter(k => typeof k === 'string');
        setPendingKeys(prev => [...new Set([...prev, ...stringKeys])]);
      }
      setApplied(true);
    } else if (pendingKeys.length === 0) {
      setSearchText("");
      setSearch("");
      setApplied(false);
    }
  }, [selectedKeys]);

  const [ applied, setApplied ] = useState(false);

  // Filtered description values by search - only show when applied is true
  const filteredDescriptions = useMemo(() => {
    if (!applied) return [];
    const searchQuery = search || '';
    const normalize = str => str.trim().replace(/\s+/g, ' ').toLowerCase();
    return [...new Set(filterValues.description || [])].filter(val =>
      val && (!searchQuery || normalize(val).includes(normalize(searchQuery)))
    );
  }, [filterValues.description, search, applied]);
const allSelected = filteredDescriptions.length > 0 && filteredDescriptions.every(v => pendingKeys.includes(v));
  
  // Handle select all
  const handleSelectAll = () => {
    const allFiltered = filteredDescriptions.map(d => d);
    setPendingKeys(prev => {
      // Keep existing selections that aren't in current filtered items
      const existingSelections = prev.filter(item => !filteredDescriptions.includes(item));
      return [...new Set([...existingSelections, ...allFiltered])];
    });
  };

  // Count of selected items
  const selectedCount = pendingKeys.length;

  return (
    <div style={{ padding: 8, width: 600, overflowY: 'auto', maxHeight: 400 }}>
      <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
        <input
          placeholder="Search description"
          value={search}
          onChange={e => {
            const newValue = e.target.value;
            setSearch(newValue);
            // Clear options if search is empty
            if (!newValue.trim()) {
              setApplied(false);
              setPendingKeys([]);
            }
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && search) {
              setSearchText(search);
              setApplied(true);
            }
          }}
          style={{ flex: 1, padding: 4, borderRadius: 4, border: '1px solid #d9d9d9' }}
          autoFocus
        />
        <Button
          type="primary"
          onClick={() => {
            setSearchText(search);
            setApplied(true);
          }}
          disabled={!search}
        >
          Search
        </Button>
      </div>
      <Button
        type="link"
        style={{ marginBottom: 5, padding: 0 }}
        onClick={() => {
          if (allSelected) {
            // Deselect all filtered items
            setPendingKeys(prev => prev.filter(item => !filteredDescriptions.includes(item)));
          } else {
            // Select all filtered items
            handleSelectAll();
          }
        }}
        disabled={filteredDescriptions.length === 0}
      >
        {allSelected && selectedCount > 0 ? "Deselect All" : "Select All"}
      </Button>
      <br />
      <div style={{ maxHeight: 200, overflowY: 'auto', marginTop: 5 }}>
        {filteredDescriptions.length === 0 ? (
          <div style={{ color: '#999', textAlign: 'center', padding: 16 }}>No results</div>
        ) : (
          filteredDescriptions.map((val) => (
            <div key={val}>
              <div
                style={{
                  padding: '4px 8px',
                  backgroundColor: pendingKeys.includes(val) ? '#e6f7ff' : 'transparent',
                  cursor: 'pointer',
                  borderRadius: 4,
                  margin: '2px 0',
                  display: 'flex',
                  alignItems: 'center'
                }}
                onClick={() => {
                  setPendingKeys(prev => {
                    const newKeys = prev.includes(val)
                      ? prev.filter(i => i !== val)
                      : [...prev, val];
                    return newKeys;
                  });
                }}
              >
                <input
                  type="checkbox"
                  checked={pendingKeys.includes(val)}
                  onChange={e => {
                    if (e.target.checked) {
                      setPendingKeys(prev => [...prev, val]);
                    } else {
                      setPendingKeys(prev => prev.filter(v => v !== val));
                    }
                  }}
                  style={{ marginRight: 8 }}
                />
                <span style={{ color: "black" }}>{val}</span>
              </div>
            </div>
          ))
        )}
      </div>
      <div style={{ 
        marginTop: 8, 
        display: 'flex', 
        justifyContent: 'space-between',
        alignItems: 'center',
        borderTop: '1px solid #f0f0f0',
        paddingTop: 8
      }}>
        <div style={{ fontSize: 12, color: '#666' }}>
          {selectedCount > 0 ? `${selectedCount} selected` : ''}
        </div>
        <div>
          
          <Button
            onClick={() => {
              clearFilters();
              setSearch('');
              setPendingKeys([]);
              confirm();
            }}
            size="small"
            style={{ marginRight: 8 }}
          >
            Reset
          </Button>
          
          <Button
            type="primary"
            onClick={() => {
              const MAX_URL_LENGTH = 1000; 
              const joinedDescriptions = pendingKeys.join(',');

              if (joinedDescriptions.length <= MAX_URL_LENGTH) {
                // Send all selected descriptions
                setSelectedKeys(pendingKeys.length > 0 ? pendingKeys : (search ? [search] : []));
              } else {

                setSelectedKeys(search ? [search] : []);
              }

              confirm();
            }}
            size="small"
          >
            OK
          </Button>
        </div>
      </div>
    </div>
  );
}
